/** @jsxImportSource preact */
import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import * as echarts from 'echarts';
import { radarBandColors } from '../data/impactScale';
import { RadarScaleKey } from './RadarScaleKey';
import { wrapAxisName, radarRadius } from './radarLayout';

export interface NatSecRadarCategory {
  label: string;
  score: number | null;
}

interface NatSecRadarProps {
  categories: NatSecRadarCategory[];
  /** Canvas height in px. Default 260. */
  height?: number;
}

/**
 * Client-rendered replacement for the pre-generated "NatSec Spider" images:
 * a radar of the five national-security impact-category ratings (1–5), one
 * polygon in neutral ink over five concentric bands tinted with the impact
 * scale (Negligible → Critical, centre outwards). The RadarScaleKey beneath
 * names each band, so the scale is read from words, not ring numbers. Used
 * by the NatSec detail modal for datasets that carry the per-category scores
 * but no spider attachment (the Arcola mirror). No tooltip, for parity with
 * SeverityRadar — the category rows below carry the exact ratings.
 */
export function NatSecRadar({ categories, height = 260 }: NatSecRadarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current || categories.length < 3) return;

    const el = ref.current;
    const chart = echarts.init(el);
    chartRef.current = chart;
    // Radius follows the container so the names fit on a phone (see radarLayout).
    const radius = () => radarRadius(el.clientWidth, el.clientHeight, 0.64, 80);

    chart.setOption({
      color: ['#111827'],
      tooltip: { show: false },
      radar: {
        indicator: categories.map((c) => ({ name: c.label, max: 5 })),
        radius: radius(),
        center: ['50%', '52%'],
        axisName: { fontSize: 10, color: '#6b7280', lineHeight: 12, formatter: (name?: string) => wrapAxisName(name ?? '') },
        splitNumber: 5,
        axisLine: { lineStyle: { color: 'rgba(0,0,0,0.12)' } },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.9)', width: 1 } },
        splitArea: { areaStyle: { color: radarBandColors() } },
      },
      series: [
        {
          type: 'radar',
          // Not interactive: without this the polygon shows a pointer
          // cursor on hover, as if it linked somewhere.
          silent: true,
          symbolSize: 5,
          data: [
            {
              name: 'National Security Impact',
              value: categories.map((c) => c.score ?? 0),
              lineStyle: { width: 2 },
              areaStyle: { opacity: 0.12 },
            },
          ],
        },
      ],
    });

    const ro = new ResizeObserver(() => {
      chart.setOption({ radar: { radius: radius() } });
      chart.resize();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [JSON.stringify(categories)]);

  if (categories.length < 3) return null; // a radar needs ≥3 axes to read

  return (
    <div>
      <div
        ref={ref}
        role="img"
        aria-label="National Security Impact Profile — per-category impact ratings, Negligible to Critical"
        style={{ width: '100%', height: `${height}px` }}
      />
      <RadarScaleKey variant="natsec" />
    </div>
  );
}
